// useAuthenticatedUser.ts
// Phase 3 (2026-05-25) — single source of truth for the displayed user
// identity in the Trading UI.
// Phase 4 (2026-05-25) — extended with a permissions matrix derived from
// the user's account status, and helper hooks for feature-gating UI.
// M-final  (2026-06-05) — added useUserDisplay() composer that combines
// the localStorage identity with the bearer-derived email from
// /api/v1/user/portfolio (via useViewerPortfolio) so the top-right pill,
// dropdown, sidebar, and billing cardholder can ALL render the real
// authenticated user without ever leaking the historical "JD" / "John
// Doe" placeholder.

import { useViewerPortfolio } from './useViewerPortfolio';

// User statuses per Phase 4 WO:
export type UserStatus =
  | 'email_unverified'
  | 'security_incomplete'
  | 'profile_incomplete'
  | 'questionnaire_incomplete'
  | 'pending_review'
  | 'approved_demo'
  | 'approved_paper'
  | 'admin'
  | 'suspended'
  | 'closed';

// Legacy alias used by Phase 3 — kept for back-compat.
export type AccountStatus = 'demo' | 'paper-only' | 'admin';

export type Feature =
  | 'dashboard'              // home/landing tile (demo gets a limited view)
  | 'ai_tutor'               // always allowed
  | 'market_pulse'           // always allowed
  | 'matrix'                 // observation-only Matrix dashboard — always allowed
  | 'markets'                // markets browse — always allowed
  | 'academy'                // education — always allowed
  | 'portfolio'              // paper portfolio (empty unless paper connected)
  | 'risk_center'            // risk surface (empty unless paper connected)
  | 'strategy_lab'           // limited for demo, full for paper/admin
  | 'council_debate'         // limited for demo (read-only), interactive for paper/admin
  | 'simulation'             // read-only for normal users; editable only for admin
  | 'simulation_edit'        // explicit edit permission
  | 'billing'                // hidden/disabled
  | 'api_integrations'       // locked until paper-approved
  | 'live_trading'           // BLOCKED for everyone at this layer
  | 'admin_review'           // admin-only
  | 'paper_account_request'  // approved_demo can request paper
;

export interface PermissionMatrix {
  dashboard: boolean;
  ai_tutor: boolean;
  market_pulse: boolean;
  matrix: boolean;
  markets: boolean;
  academy: boolean;
  portfolio: boolean;
  risk_center: boolean;
  strategy_lab: boolean;
  council_debate: boolean;
  simulation: boolean;
  simulation_edit: boolean;
  billing: boolean;
  api_integrations: boolean;
  live_trading: false;       // permanent — see acceptance criteria
  admin_review: boolean;
  paper_account_request: boolean;
}

export interface AuthenticatedUser {
  firstName: string;
  lastName: string;
  initials: string;
  // M-final 2026-06-05 — surface the real demo user's email so the
  // sidebar / top-right badge can fall back to email when no name is
  // present. Read from localStorage `adversyn:auth-user` if the auth
  // flow persisted it; otherwise resolved from /api/v1/user/portfolio
  // via the useUserDisplay() composer below.
  email?: string | null;
  accountId: string | null;
  /** Phase 4 fine-grained status. */
  userStatus: UserStatus;
  /** Phase 3 coarse-grained status, kept for back-compat. */
  accountStatus: AccountStatus;
  accountStatusLabel: string;
  paperAccountConnected: boolean;
  liveTradingAllowed: false;
  /** Feature → allowed boolean. */
  permissions: PermissionMatrix;
}

const ALWAYS_DENIED: Pick<PermissionMatrix, 'live_trading'> = {
  live_trading: false,
};

// Status × feature matrix. Frontend uses this to gate UI; backend mirrors
// the same matrix in /backend_staging/permissions.py (staged only).
export function computePermissions(status: UserStatus): PermissionMatrix {
  // Base "denied" set
  const base: PermissionMatrix = {
    dashboard: false,
    ai_tutor: false,
    market_pulse: false,
    matrix: false,
    markets: false,
    academy: false,
    portfolio: false,
    risk_center: false,
    strategy_lab: false,
    council_debate: false,
    simulation: false,
    simulation_edit: false,
    billing: false,
    api_integrations: false,
    admin_review: false,
    paper_account_request: false,
    ...ALWAYS_DENIED,
  };

  if (status === 'closed' || status === 'suspended') {
    // No app features at all. They get a "your account is suspended/closed" view.
    return base;
  }

  // From email_unverified through pending_review: very limited (mostly
  // restricted to AI Tutor + Academy + Market Pulse landing-ish).
  if (status === 'email_unverified' || status === 'security_incomplete' ||
      status === 'profile_incomplete' || status === 'questionnaire_incomplete' ||
      status === 'pending_review') {
    return {
      ...base,
      ai_tutor: true,
      academy: true,
      market_pulse: true,
      markets: true,
    };
  }

  if (status === 'approved_demo') {
    return {
      ...base,
      dashboard: true,
      ai_tutor: true,
      market_pulse: true,
      matrix: true,
      markets: true,
      academy: true,
      portfolio: true,         // visible but shows "no paper account" empty state
      risk_center: true,       // visible but shows empty state
      strategy_lab: true,      // limited mode
      council_debate: true,    // read-only mode
      simulation: true,        // read-only mode
      simulation_edit: false,
      paper_account_request: true,
      // billing, api_integrations, admin_review, live_trading remain false
    };
  }

  if (status === 'approved_paper') {
    return {
      ...base,
      dashboard: true,
      ai_tutor: true,
      market_pulse: true,
      matrix: true,
      markets: true,
      academy: true,
      portfolio: true,
      risk_center: true,
      strategy_lab: true,
      council_debate: true,
      simulation: true,
      simulation_edit: false,    // still admin-only
      api_integrations: true,    // paper-only keys
      paper_account_request: false, // already has one
      // billing remains false until a real plan exists
      // live_trading permanently false
    };
  }

  if (status === 'admin') {
    return {
      ...base,
      dashboard: true,
      ai_tutor: true,
      market_pulse: true,
      matrix: true,
      markets: true,
      academy: true,
      portfolio: true,
      risk_center: true,
      strategy_lab: true,
      council_debate: true,
      simulation: true,
      simulation_edit: true,
      billing: true,             // admin can see billing surface
      api_integrations: true,
      admin_review: true,        // admin-only
      paper_account_request: false,
      // live_trading permanently false at this layer
    };
  }

  return base;
}

const STATUS_LABEL: Record<UserStatus, string> = {
  email_unverified: 'Email Unverified',
  security_incomplete: 'Security Setup Incomplete',
  profile_incomplete: 'Profile Incomplete',
  questionnaire_incomplete: 'Questionnaire Incomplete',
  pending_review: 'Pending Review',
  approved_demo: 'Demo Mode',
  approved_paper: 'Paper-Only',
  admin: 'Admin',
  suspended: 'Suspended',
  closed: 'Closed',
};

// M-final 2026-06-05 — the demo-user fallback identity. Never invents a
// real-sounding name; never "JD"/"John Doe". The neutral "Demo User" +
// "DU" initials are surfaced ONLY when no authenticated identity is
// available (no localStorage record, no backend email).
const DEFAULT_DEMO_USER: AuthenticatedUser = {
  firstName: 'Demo',
  lastName: 'User',
  initials: 'DU',
  email: null,
  accountId: null,
  userStatus: 'approved_demo',
  accountStatus: 'demo',
  accountStatusLabel: STATUS_LABEL.approved_demo,
  paperAccountConnected: false,
  liveTradingAllowed: false,
  permissions: computePermissions('approved_demo'),
};

// Reject placeholder-style identities. Any incoming "John Doe" / "JD"
// (e.g. from a stale localStorage write that pre-dates this milestone)
// is coerced back to the neutral demo identity so we never re-leak the
// fake name into the UI.
const PLACEHOLDER_NAMES = new Set(['johndoe', 'john doe', 'jd']);

function _isPlaceholderName(s: string): boolean {
  return PLACEHOLDER_NAMES.has(s.toLowerCase().replace(/\s+/g, ' ').trim());
}

function coerceUserStatus(raw: unknown): UserStatus {
  const known: UserStatus[] = [
    'email_unverified', 'security_incomplete', 'profile_incomplete',
    'questionnaire_incomplete', 'pending_review', 'approved_demo',
    'approved_paper', 'admin', 'suspended', 'closed',
  ];
  return known.includes(raw as UserStatus) ? (raw as UserStatus) : 'approved_demo';
}

function legacyFor(status: UserStatus): AccountStatus {
  if (status === 'admin') return 'admin';
  if (status === 'approved_paper') return 'paper-only';
  return 'demo';
}

export function useAuthenticatedUser(): AuthenticatedUser {
  try {
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem('adversyn:auth-user');
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<AuthenticatedUser>;
        // M-final 2026-06-05 — coerce any placeholder "John Doe"/"JD"
        // back to the neutral demo identity. Old localStorage records
        // written before this milestone could still contain them.
        // WO10 full_name parse (2026-06-06) — backend /auth/me now returns
        // full_name (from auth_demo_metadata). When firstName / lastName
        // are absent, derive them by splitting full_name on the first
        // whitespace. This is the only way to get "Darren Nelson" -> DN
        // because the users table doesn't have first/last columns.
        const rawFullName = String((parsed as any).full_name || (parsed as any).fullName || '').trim();
        const _fnParts = rawFullName ? rawFullName.split(/\s+/) : [];
        const _fnFirst = _fnParts.length >= 1 ? _fnParts[0] : '';
        const _fnLast = _fnParts.length >= 2 ? _fnParts.slice(1).join(' ') : '';
        const rawFirst = String(parsed.firstName || _fnFirst || '');
        const rawLast = String(parsed.lastName || _fnLast || '');
        const rawInits = String(parsed.initials || '');
        const looksFake =
          _isPlaceholderName(rawFirst + ' ' + rawLast) ||
          _isPlaceholderName(rawInits) ||
          _isPlaceholderName(rawFirst) ||
          _isPlaceholderName(rawLast);
        const firstName = looksFake ? DEFAULT_DEMO_USER.firstName
          : (rawFirst || DEFAULT_DEMO_USER.firstName);
        const lastName = looksFake ? DEFAULT_DEMO_USER.lastName
          : (rawLast || DEFAULT_DEMO_USER.lastName);
        // WO8 — never trust rawInits from localStorage; always derive
        // from firstName + lastName. This guarantees the single-name case
        // (Darren) returns D, not DA.
        const _derivedInits = (firstName && lastName)
          ? (firstName.charAt(0) + lastName.charAt(0)).toUpperCase()
          : (firstName ? firstName.charAt(0).toUpperCase()
            : (lastName ? lastName.charAt(0).toUpperCase() : ''));
        const initials = (!_derivedInits || _isPlaceholderName(_derivedInits))
          ? DEFAULT_DEMO_USER.initials
          : _derivedInits;
        const email = typeof parsed.email === 'string' && parsed.email.trim()
          ? parsed.email.trim()
          : null;
        const userStatus = coerceUserStatus(parsed.userStatus ?? parsed.accountStatus);
        return {
          firstName,
          lastName,
          initials,
          email,
          accountId: parsed.accountId ?? null,
          userStatus,
          accountStatus: legacyFor(userStatus),
          accountStatusLabel: STATUS_LABEL[userStatus],
          paperAccountConnected: Boolean(parsed.paperAccountConnected),
          liveTradingAllowed: false,
          permissions: computePermissions(userStatus),
        };
      }
    }
  } catch {
    /* fall through to default */
  }
  return DEFAULT_DEMO_USER;
}

// ── User display helpers (M-final 2026-06-05) ─────────────────────
// One normalized identity surface that ALL profile-rendering callers
// (top-right pill, sidebar profile card, account header, billing
// cardholder) MUST use instead of reading firstName/lastName/initials
// directly. Guarantees:
//   - never returns "John Doe" / "JD"
//   - prefers a real name when present
//   - falls back to email (the demo flow stores email upstream),
//     using the full email as the display name and the first 2 chars
//     of the email's local part for the initials
//   - finally falls back to neutral "Demo User" / "DU"

export interface UserDisplayIdentity {
  displayName: string;
  displayInitials: string;
  email: string | null;
  accountStatusLabel: string;
  source: 'auth_name' | 'auth_email' | 'backend_email' | 'demo_default';
}

export function deriveDisplayName(
  firstName: string,
  lastName: string,
  email: string | null | undefined,
): { name: string; source: UserDisplayIdentity['source'] } {
  const combined = (firstName + ' ' + lastName).trim();
  if (combined && combined !== 'Demo User' && !_isPlaceholderName(combined)) {
    return { name: combined, source: 'auth_name' };
  }
  if (email && email.trim() && !_isPlaceholderName(email)) {
    return { name: email.trim(), source: 'auth_email' };
  }
  return { name: 'Demo User', source: 'demo_default' };
}

export function deriveDisplayInitials(name: string, email: string | null | undefined): string {
  // Reject obvious placeholders defensively.
  if (_isPlaceholderName(name)) return 'DU';
  const cleaned = name.trim();
  if (cleaned.includes('@')) {
    // WO8 initials single-name (2026-06-05) — email branch returns the
    // first alpha letter only of the local part (D for darren@x.com),
    // NOT the first two letters of first name (which used to give DA).
    const local = cleaned.split('@')[0].replace(/[^A-Za-z]/g, '');
    const out = (local.charAt(0) || 'DU').toUpperCase();
    return _isPlaceholderName(out) ? 'DU' : out;
  }
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const out = (parts[0].charAt(0) + parts[1].charAt(0)).toUpperCase();
    return _isPlaceholderName(out) ? 'DU' : out;
  }
  if (parts.length === 1 && parts[0]) {
    // WO8 single-name returns the first letter only (Darren → D, not DA).
    const out = parts[0].charAt(0).toUpperCase();
    return _isPlaceholderName(out) ? 'DU' : out;
  }
  if (email && email.trim()) return deriveDisplayInitials(email.trim(), null);
  return 'DU';
}

/** Build a UserDisplayIdentity from a (possibly-empty) auth user and a
 * (possibly-empty) backend email override. Pure — does not call hooks,
 * safe to use from anywhere.
 */
export function composeUserDisplay(
  user: AuthenticatedUser,
  backendEmail?: string | null,
): UserDisplayIdentity {
  // Email precedence: backend (from /user/portfolio) > auth localStorage > null
  const resolvedEmail =
    (backendEmail && backendEmail.trim()) ? backendEmail.trim()
    : (user.email && user.email.trim()) ? user.email.trim()
    : null;
  const derived = deriveDisplayName(user.firstName, user.lastName, resolvedEmail);
  const displayInitials = deriveDisplayInitials(derived.name, resolvedEmail);
  // Refine source: if name was a placeholder/empty but backend email
  // exists, mark the source as backend_email for telemetry.
  const source: UserDisplayIdentity['source'] =
    derived.source === 'auth_email' && backendEmail && backendEmail.trim()
      ? 'backend_email'
      : derived.source;
  return {
    displayName: derived.name,
    displayInitials,
    email: resolvedEmail,
    accountStatusLabel: user.accountStatusLabel,
    source,
  };
}

/** Convenience: returns true iff the current user has the named feature. */
export function useFeature(feature: Feature): boolean {
  const u = useAuthenticatedUser();
  return Boolean((u.permissions as unknown as Record<string, boolean>)[feature]);
}

/** M-final 2026-06-05 — single normalized identity hook for ALL profile
 * UI surfaces. Internally consumes useAuthenticatedUser() for the
 * localStorage-backed identity and useViewerPortfolio() for the
 * bearer-derived email echoed by /api/v1/user/portfolio. Returns a
 * UserDisplayIdentity whose displayName / displayInitials never leak
 * a placeholder ("John Doe" / "JD").
 *
 * Required by:
 *   - App.tsx top-right account badge (the "JD" pill at line ~1884)
 *   - App.tsx profile dropdown
 *   - AccountPortal sidebar identity card
 *   - BillingSubscription cardholder
 */
export function useUserDisplay(): UserDisplayIdentity {
  const u = useAuthenticatedUser();
  const viewer = useViewerPortfolio();
  const backendEmail = (viewer?.data as any)?.email || null;
  return composeUserDisplay(u, backendEmail);
}

export const NO_PAPER_ACCOUNT_COPY = {
  heading: 'No paper account connected.',
  body: 'Connect an approved paper trading account to enable paper portfolio data.',
  liveDisabled: 'Real-money brokerage connections are not available.',
} as const;

export const RESTRICTED_FEATURE_COPY = {
  heading: 'Feature not available for your account status.',
  body: 'Your current account status does not include this feature. Complete onboarding or contact an Adversyn admin if you believe this is in error.',
} as const;

// 2026-06-06 source-of-truth-full-ui — when the viewer has an
// Alpaca paper broker row, the empty-state copy must NOT say "No
// paper account connected.". Callers should consult
// resolveAccountStatusCopy(viewer) instead of the legacy const.
export const ALPACA_PAPER_CONNECTED_COPY = {
  heading: 'Alpaca Paper connected.',
  body: 'Mode: Paper \u2014 Live brokerage: Not enabled.',
  liveDisabled: 'Real-money brokerage connections are not enabled.',
} as const;

export function resolveAccountStatusCopy(viewer: any) {
  const connected = Boolean(viewer?.broker_connected) ||
    viewer?.source === 'alpaca_paper_self_connected' ||
    viewer?.broker_connection_connected;
  return connected ? ALPACA_PAPER_CONNECTED_COPY : NO_PAPER_ACCOUNT_COPY;
}

